import { appendCodeToPhoneNumber } from 'src/components/Forms/utils';
import { BrandGroupUpdatePatch } from 'src/graphql/entities/brand-group';
import { VoiceChannelSettingFormFields, VoiceFormModel } from './types';

/** Each phone number field paired with the extension it owns. */
const PHONE_EXT_FIELDS = [
  [
    VoiceChannelSettingFormFields.PhonePublished,
    VoiceChannelSettingFormFields.PhonePublishedExt,
  ],
  [VoiceChannelSettingFormFields.PhoneReroute, VoiceChannelSettingFormFields.PhoneRerouteExt],
] as const;

const EXT_FIELDS: ReadonlySet<keyof VoiceFormModel> = new Set(
  PHONE_EXT_FIELDS.map(([, extField]) => extField),
);

/**
 * Maps Voice settings form values to a brand group patch. Clearing a phone number also
 * clears its extension: saves are per changed field, so without this a stale extension
 * would reattach when a new number is set later.
 */
export function toVoiceBrandGroupPatch(formValue: VoiceFormModel): BrandGroupUpdatePatch {
  const patch = Object.fromEntries(
    Object.entries(formValue).map(([key, value]) => [
      key,
      parsePhoneNumber(key as keyof VoiceFormModel, value),
    ]),
  ) as BrandGroupUpdatePatch;

  for (const [phoneField, extField] of PHONE_EXT_FIELDS) {
    if (phoneField in formValue && !formValue[phoneField]) {
      patch[extField] = null;
    }
  }

  return patch;
}

function parsePhoneNumber(key: keyof VoiceFormModel, value: string) {
  if (key === VoiceChannelSettingFormFields.Id) {
    return value;
  }

  if (!value) {
    return null;
  }

  if (EXT_FIELDS.has(key)) {
    const parsedExt = parseInt(value, 10);
    return Number.isNaN(parsedExt) ? null : parsedExt;
  }

  if (key === VoiceChannelSettingFormFields.CallerIdPhone) {
    return parseInt(appendCodeToPhoneNumber(value));
  }

  return parseInt(value);
}
